import React, { useState } from "react";
import MFooterNavigation from "@/components/ui/atoms/MFooterNavigation";
import { MProfilePicker } from "@/components/ui/atoms/MProfilePicker";
import { Card } from "@/components/ui/molecules/card";
import { useTranslation } from "react-i18next";

/**
 * Demo page showcasing the integration of MNavigationBar and MProfilePicker components
 * in a mobile layout with proper positioning and interaction.
 */
const IntegratedMobileDemo: React.FC = () => {
  useTranslation(); // Initialize i18n
  const [activeSection, setActiveSection] = useState("home");

  // Generate dummy content for each section
  const renderContent = () => {
    switch (activeSection) {
      case "home":
        return (
          <div className="space-y-4">
            <h2 className="text-xl font-bold">Home</h2>
            <p>This is the home section of the mobile app.</p>
            {Array(5)
              .fill(0)
              .map((_, i) => (
                <Card key={i} className="p-4">
                  <h3 className="font-medium mb-2">Home Card {i + 1}</h3>
                  <p className="text-gray-600 text-sm">
                    This is a sample card that would appear in the home section.
                  </p>
                </Card>
              ))}
          </div>
        );
      case "documents":
        return (
          <div className="space-y-4">
            <h2 className="text-xl font-bold">Documents</h2>
            <p>This is the documents section of the mobile app.</p>
            {Array(3)
              .fill(0)
              .map((_, i) => (
                <Card key={i} className="p-4">
                  <h3 className="font-medium mb-2">Document {i + 1}</h3>
                  <p className="text-gray-600 text-sm">
                    This is a sample document that would appear in the documents
                    section.
                  </p>
                </Card>
              ))}
          </div>
        );
      case "insights":
        return (
          <div className="space-y-4">
            <h2 className="text-xl font-bold">Insights</h2>
            <p>This is the insights section of the mobile app.</p>
            {Array(4)
              .fill(0)
              .map((_, i) => (
                <Card key={i} className="p-4">
                  <h3 className="font-medium mb-2">Insight {i + 1}</h3>
                  <p className="text-gray-600 text-sm">
                    This is a sample insight that would appear in the insights
                    section.
                  </p>
                </Card>
              ))}
          </div>
        );
      case "reminders":
        return (
          <div className="space-y-4">
            <h2 className="text-xl font-bold">Reminders</h2>
            <p>This is the reminders section of the mobile app.</p>
            {Array(2)
              .fill(0)
              .map((_, i) => (
                <Card key={i} className="p-4">
                  <h3 className="font-medium mb-2">Reminder {i + 1}</h3>
                  <p className="text-gray-600 text-sm">
                    This is a sample reminder that would appear in the reminders
                    section.
                  </p>
                </Card>
              ))}
          </div>
        );
      default:
        return <div>Select a section</div>;
    }
  };

  // Handle navigation item click
  const handleNavigationClick = (item: any) => {
    setActiveSection(item.id);
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col relative">
      {/* Header with MProfilePicker */}
      <header className="sticky top-0 z-40 bg-white shadow-sm px-4 py-3 flex items-center justify-between">
        <h1 className="text-lg font-semibold text-purple-900">Maatra App</h1>
        <MProfilePicker />
      </header>

      {/* Main content area */}
      <main className="flex-1 p-4 pb-24 overflow-auto">
        <div className="max-w-md mx-auto">
          <Card className="p-4 mb-6">
            <h2 className="text-lg font-medium mb-2">Integrated Mobile Demo</h2>
            <p className="text-gray-600 text-sm">
              This demo showcases the MNavigationBar and MProfilePicker
              components working together in a mobile layout. The navigation bar
              stays fixed at the bottom, while the profile picker is positioned
              in the header. Try scrolling and switching between sections.
            </p>
          </Card>

          {/* Dynamic content based on selected navigation item */}
          {renderContent()}

          {/* Add extra content to enable scrolling */}
          <div className="h-20"></div>
        </div>
      </main>

      {/* Fixed bottom navigation */}
      <MFooterNavigation
        onItemClick={handleNavigationClick}
        storageKey="integrated-demo-nav"
      />
    </div>
  );
};

export default IntegratedMobileDemo;
